import { useCallback } from 'react';
import * as XLSX from 'xlsx';
import { IPDFReportData } from '../models/pdfReport';
import { format } from 'date-fns';
import { coordinatesToString } from '../utils/cordinates_helpers';

export const useExcelExport = (data: IPDFReportData) => {
    const exportToExcel = useCallback(async () => {
        for (const project of data.projects) {
            const workbook = XLSX.utils.book_new();
            // Sheet 1: Project Summary
            const summaryData = [
                ['PROJECT SUMMARY'],
                ['Project Name', project.name],
                ['Report Date', format(data.date, 'MMMM do, yyyy')],
                ['Districts', project.districts.join(', ') || 'N/A'],
                ['Total Weight Collected', `${project.collectedWeight.total} kg`],
                ['Total Cleanups', project.cleanUps.length],
                [],
                ['WASTE TYPES BREAKDOWN'],
                ['Type', 'Weight (kg)'],
                ...project.collectedWeight.wasteTypes.map(wt => [wt.name, wt.weight]),
                [],
                ['CATEGORY ITEMS BREAKDOWN'],
                ['Category', 'Item', 'Amount'],
                ...project.collectedCategories.flatMap(cat => 
                    cat.items.map(item => [cat.name, item.name, item.amount])
                ),
                [],
                ['RESOURCES USED'],
                ['Resource', 'Amount'],
                ...project.usedResources.map(res => [res.name, res.items])
            ];

            const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
            XLSX.utils.book_append_sheet(workbook, summarySheet, 'Summary');

            // Sheet 2: Detailed Cleanup Basic Info
            const basicInfoHeaders = [
                'Location Name',
                'Coordinates',
                'Activity',
                'Creator',
                'Date',
                'Duration From',
                'Duration To',
                'Participants',
                'Area (m²)',
                'Length (m)',
                'District',
                'Municipality',
                'Site Types',
                'Substrate',
                'Additional Location Info',
                'No Litter Found',
                'Notes'
            ];

            const basicInfoData = [
                basicInfoHeaders,
                ...project.cleanUps.map(cleanup => [
                    cleanup.location_name || 'N/A',
                    coordinatesToString(cleanup.location),
                    cleanup.activityName || 'N/A',
                    cleanup.creatorName || 'N/A',
                    format(new Date(cleanup.date), 'dd-MM-yyyy'),
                    cleanup.duration?.from ? `${cleanup.duration.from.hour}:${cleanup.duration.from.minute}` : 'N/A',
                    cleanup.duration?.to ? `${cleanup.duration.to.hour}:${cleanup.duration.to.minute}` : 'N/A',
                    cleanup.num_of_participant || 'N/A',
                    cleanup.area || 'N/A',
                    cleanup.length || 'N/A',
                    cleanup.district || 'N/A',
                    cleanup.municipality || 'N/A',
                    cleanup.site_types?.join(', ') || 'N/A',
                    cleanup.substrateTitle || 'N/A',
                    cleanup.additionalLocationInfoNames?.join(', ') || 'N/A',
                    cleanup.no_litter_found ? 'Yes' : 'No',
                    cleanup.notes || 'No notes'
                ])
            ];

            const basicInfoSheet = XLSX.utils.aoa_to_sheet(basicInfoData);
            XLSX.utils.book_append_sheet(workbook, basicInfoSheet, 'Clean Up Info');

            // Sheet 3: Collected Weight by Cleanup
            // Get all unique waste types for column headers
            const wasteTypeNames = project.cleanUps.flatMap(cleanup => 
                cleanup.collectedWeight.wasteTypes.map(wt => wt.name)
            );
            const uniqueWasteTypes = wasteTypeNames.filter((name, index) => wasteTypeNames.indexOf(name) === index);
            const allWasteTypes = uniqueWasteTypes.sort();

            const weightHeaders = [
                'Location Name',
                'Total Weight (kg)',
                ...allWasteTypes
            ];

            const weightData = [
                weightHeaders,
                ...project.cleanUps.map(cleanup => {
                    const row = [
                        cleanup.location_name || 'N/A',
                        cleanup.collectedWeight.total
                    ];
                    
                    // Add weight for each waste type (0 if not found)
                    allWasteTypes.forEach(wasteTypeName => {
                        const wasteType = cleanup.collectedWeight.wasteTypes.find(wt => wt.name === wasteTypeName);
                        row.push(wasteType ? wasteType.weight : 0);
                    });
                    
                    return row;
                })
            ];

            const weightSheet = XLSX.utils.aoa_to_sheet(weightData);
            XLSX.utils.book_append_sheet(workbook, weightSheet, 'Collected Weight');

            // Sheet 4: Category Items by Cleanup
            const categoryHeaders = [
                'Location Name',
                'Category',
                'Item',
                'Amount'
            ];

            const categoryData = [
                categoryHeaders,
                ...project.cleanUps.flatMap(cleanup =>
                    cleanup.collectedCategories.flatMap(category =>
                        category.items.map(item => [
                            cleanup.location_name || 'N/A',
                            category.name,
                            item.name,
                            item.amount
                        ])
                    )
                )
            ];

            const categorySheet = XLSX.utils.aoa_to_sheet(categoryData);
            XLSX.utils.book_append_sheet(workbook, categorySheet, 'Category Items');

            // Sheet 5: Resources Used by Cleanup
            const resourceHeaders = [
                'Location Name',
                'Resource Type',
                'Amount Used'
            ];

            const resourceData = [
                resourceHeaders,
                ...project.cleanUps.flatMap(cleanup =>
                    cleanup.usedResources.map(resource => [
                        cleanup.location_name || 'N/A',
                        resource.name,
                        resource.items
                    ])
                ),
                [],
                ['TOTALS (shared resources counted once at max value)'],
                resourceHeaders,
                ...project.usedResources.map(resource => [
                    'TOTAL',
                    resource.name,
                    resource.items,
                ]),
            ];

            const resourceSheet = XLSX.utils.aoa_to_sheet(resourceData);
            XLSX.utils.book_append_sheet(workbook, resourceSheet, 'Resources Used');

            // Generate filename and download for each project
            const filename = `${project.name} - Report of ${format(data.date, 'MMMM do, yyyy')}.xlsx`;
            XLSX.writeFile(workbook, filename);

            // Small delay between downloads
            await new Promise(resolve => setTimeout(resolve, 500));
        }
    }, [data]);

    return { exportToExcel };
};